import { useEffect, useRef } from 'react';
import { isSessionReplayRunning, startSessionReplay, stopSessionReplay } from '@pegasus/sentry';
export var REPLAY_LEAVE_GRACE_TIMEOUT_MS = 3 * 1000;
export var REPLAY_MIN_IN_VIEW_TIMEOUT_MS = 1000;
var visibleItemsByFeature = new Map();
var replayLeaveTimeoutByFeature = new Map();
var visibleItemTimeoutByFeature = new Map();
var getVisibleItems = function (feature) {
    if (!visibleItemsByFeature.has(feature)) {
        visibleItemsByFeature.set(feature, new Set());
    }
    return visibleItemsByFeature.get(feature);
};
var clearReplayLeaveTimeout = function (feature) {
    var timeout = replayLeaveTimeoutByFeature.get(feature);
    if (!timeout) {
        return;
    }
    clearTimeout(timeout);
    replayLeaveTimeoutByFeature.delete(feature);
};
var getVisibleItemTimeouts = function (feature) {
    if (!visibleItemTimeoutByFeature.has(feature)) {
        visibleItemTimeoutByFeature.set(feature, new Map());
    }
    return visibleItemTimeoutByFeature.get(feature);
};
var clearVisibleItemTimeout = function (feature, itemId) {
    var visibleItemTimeouts = getVisibleItemTimeouts(feature);
    var timeout = visibleItemTimeouts.get(itemId);
    if (!timeout) {
        return;
    }
    clearTimeout(timeout);
    visibleItemTimeouts.delete(itemId);
};
var clearAllVisibleItemTimeouts = function (feature) {
    var visibleItemTimeouts = getVisibleItemTimeouts(feature);
    visibleItemTimeouts.forEach(function (timeout) {
        clearTimeout(timeout);
    });
    visibleItemTimeouts.clear();
};
var stopReplay = function (feature) {
    clearReplayLeaveTimeout(feature);
    if (isSessionReplayRunning(feature)) {
        stopSessionReplay(feature);
    }
};
var startReplay = function (feature) {
    if (!isSessionReplayRunning(feature)) {
        startSessionReplay(feature);
    }
    clearReplayLeaveTimeout(feature);
};
var resetFeatureReplayState = function (feature) {
    clearAllVisibleItemTimeouts(feature);
    getVisibleItems(feature).clear();
    stopReplay(feature);
};
var onVisibilityChange = function (itemId, isVisible, feature) {
    var visibleItems = getVisibleItems(feature);
    if (isVisible) {
        clearReplayLeaveTimeout(feature);
        if (!visibleItems.has(itemId)) {
            var visibleItemTimeouts = getVisibleItemTimeouts(feature);
            if (!visibleItemTimeouts.has(itemId)) {
                var timeout = setTimeout(function () {
                    clearVisibleItemTimeout(feature, itemId);
                    visibleItems.add(itemId);
                    if (visibleItems.size === 1) {
                        startReplay(feature);
                    }
                }, REPLAY_MIN_IN_VIEW_TIMEOUT_MS);
                visibleItemTimeouts.set(itemId, timeout);
            }
        }
        return;
    }
    clearVisibleItemTimeout(feature, itemId);
    visibleItems.delete(itemId);
    if (visibleItems.size === 0) {
        clearReplayLeaveTimeout(feature);
        if (isSessionReplayRunning(feature)) {
            var leaveTimeout = setTimeout(function () {
                stopReplay(feature);
            }, REPLAY_LEAVE_GRACE_TIMEOUT_MS);
            replayLeaveTimeoutByFeature.set(feature, leaveTimeout);
        }
    }
};
export var useVisibilitySessionReplay = function (_a) {
    var itemId = _a.itemId, isEnabled = _a.isEnabled, inView = _a.inView, inViewRef = _a.inViewRef, replayFeature = _a.replayFeature;
    var inViewSnapshot = useRef(inView);
    inViewSnapshot.current = inView;
    useEffect(function () {
        if (!isEnabled) {
            return;
        }
        var itemElement = document.getElementById(itemId);
        if (!itemElement) {
            return;
        }
        inViewRef(itemElement);
        return function () {
            inViewRef(null);
        };
    }, [itemId, inViewRef, isEnabled]);
    useEffect(function () {
        if (!isEnabled) {
            return;
        }
        var handleVisibilityChange = function () {
            if (document.hidden) {
                resetFeatureReplayState(replayFeature);
            }
            else {
                onVisibilityChange(itemId, inViewSnapshot.current, replayFeature);
            }
        };
        var handlePageHide = function () {
            resetFeatureReplayState(replayFeature);
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('pagehide', handlePageHide);
        return function () {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, [isEnabled, replayFeature, itemId]);
    useEffect(function () {
        if (!isEnabled) {
            return;
        }
        onVisibilityChange(itemId, inView, replayFeature);
    }, [inView, isEnabled, itemId, replayFeature]);
    useEffect(function () {
        if (!isEnabled) {
            return;
        }
        return function () {
            onVisibilityChange(itemId, false, replayFeature);
        };
    }, [isEnabled, itemId, replayFeature]);
};
